import jsPDF from "jspdf";

// ─── Date helpers ─────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return dateStr; }
};

const formatDateForFile = (dateStr) => {
  if (!dateStr) return new Date().toISOString().split("T")[0];
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return new Date().toISOString().split("T")[0];
    return date.toISOString().split("T")[0];
  } catch { return new Date().toISOString().split("T")[0]; }
};

// Safe string coerce
const V = (v) => (v !== undefined && v !== null && v !== "" ? String(v) : "");

// ─── Main export ──────────────────────────────────────────────────────────────
export const generateAPI6DPdf = (tcdsData) => {
  try {
    const d = tcdsData || {};
    const doc = new jsPDF({ format: "a4", unit: "mm" });

    // ── Layout constants ───────────────────────────────────────────────────────
    const ML  = 10;     // left margin
    const PW  = 210;    // page width
    const CW  = 190;    // content width
    const RH  = 5.4;    // standard row height
    const SH  = 4.5;    // section header height
    const FS  = 5.8;    // base font size

    doc.setLineWidth(0.1);

    // ── fitText: clip text to fit within maxW mm ───────────────────────────────
    const fitText = (text, maxW, fs) => {
      if (!text || String(text).length === 0) return "";
      doc.setFontSize(fs);
      let t = String(text);
      while (t.length > 1 && doc.getTextWidth(t) > maxW) t = t.slice(0, -1);
      return t;
    };

    /**
     * Bordered cell: bold label + normal value on same baseline.
     * Both are clipped to stay inside the cell boundary.
     */
    const cell = (x, y, w, h, label, value, fs = FS) => {
      doc.rect(x, y, w, h);
      const ty  = y + h * 0.64;
      const pad = 1;
      const lStr = label ? String(label) : "";
      const vStr = value !== undefined && value !== null ? String(value) : "";
      doc.setFontSize(fs);
      if (lStr.length > 0) {
        doc.setFont("helvetica", "bold");
        const lFit = fitText(lStr, w - 2 * pad, fs);
        doc.text(lFit, x + pad, ty);
        const lw = doc.getTextWidth(lFit);
        if (vStr.length > 0) {
          doc.setFont("helvetica", "normal");
          const gap  = 1.2;
          const vMax = w - lw - gap - 2 * pad;
          if (vMax > 1) doc.text(fitText(vStr, vMax, fs), x + pad + lw + gap, ty);
        }
      } else if (vStr.length > 0) {
        doc.setFont("helvetica", "normal");
        doc.text(fitText(vStr, w - 2 * pad, fs), x + pad, ty);
      }
    };

    /** Bold-only label cell (no value) */
    const lblCell = (x, y, w, h, text, fs = FS) => {
      doc.rect(x, y, w, h);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(fs);
      if (text) doc.text(fitText(String(text), w - 2, fs), x + 1, y + h * 0.64);
    };

    /** Normal-text value cell */
    const valCell = (x, y, w, h, text, fs = FS) => {
      doc.rect(x, y, w, h);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(fs);
      if (text) doc.text(fitText(String(text), w - 2, fs), x + 1, y + h * 0.64);
    };

    /** Empty bordered box */
    const emptyBox = (x, y, w, h) => doc.rect(x, y, w, h);

    /** Grey section-header bar */
    const secHeader = (x, y, w, h, text, fs = 6) => {
      doc.setFillColor(210, 210, 210);
      doc.rect(x, y, w, h, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(fs);
      doc.setTextColor(0);
      doc.text(fitText(text, w - 2, fs), x + w / 2, y + h * 0.66, { align: "center" });
    };

    /** Two-column split row: [label | value] [label | value] */
    const twoCol = (y, l1, v1, l2, v2, w1 = CW * 0.5, w2 = CW * 0.5, h = RH) => {
      cell(ML,       y, w1, h, l1, v1);
      cell(ML + w1,  y, w2, h, l2, v2);
    };

    // ── Page header (reused on every page) ───────────────────────────────────
    const drawPageHeader = (pageNum, total) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Contract Review Sheet (API 6D Swing Check Valve)", PW / 2, 8, { align: "center" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6);
      doc.text(`${pageNum} of ${total}`, PW - ML, 5, { align: "right" });
      doc.text("Format No. - F/MKT/18  Rev. 01  Dtd. 22/03/2024", PW - ML, 9, { align: "right" });
      doc.setLineWidth(0.5);
      doc.line(ML, 11, ML + CW, 11);
      doc.setLineWidth(0.1);
    };

    // ═══════════════════════════════════════════════════════════════════════════
    //  PAGE 1  (1 of 3)
    // ═══════════════════════════════════════════════════════════════════════════
    drawPageHeader(1, 3);
    let Y = 13;

    // ── Top header rows ───────────────────────────────────────────────────────
    // Customer | TCDS No | Date
    cell(ML,            Y, CW * 0.45, RH, "Customer", V(d.customer));
    cell(ML + CW * 0.45, Y, CW * 0.30, RH, "TCDS No.", V(d.tcds_no));
    cell(ML + CW * 0.75, Y, CW * 0.25, RH, "Date", formatDate(d.date));
    Y += RH;

    // Mark Dept | CR label | CR Date
    cell(ML,             Y, CW * 0.25, RH, "Mark Dept.", V(d.mark_dept));
    cell(ML + CW * 0.25, Y, CW * 0.50, RH, "", "Contract Review Sheet (API 6D Swing Check Valve)");
    cell(ML + CW * 0.75, Y, CW * 0.25, RH, "CR Date", formatDate(d.cr_date));
    Y += RH;

    // P.O. No | Offer No | Project Name
    cell(ML,             Y, CW * 0.30, RH, "P. O. No", V(d.po_no));
    cell(ML + CW * 0.30, Y, CW * 0.30, RH, "Offer No -", V(d.offer_no));
    cell(ML + CW * 0.60, Y, CW * 0.40, RH, "Project Name", V(d.project_name));
    Y += RH;

    // P.O. RECEIVED ON | TCDS RELEASED ON
    twoCol(Y, "P.O. RECEIVED ON", formatDate(d.po_received_on), "TCDS RELEASED ON", formatDate(d.tcds_released_on));
    Y += RH + 1;

    // ── GENERAL & NECESSARY REQUIREMENTS ─────────────────────────────────────
    secHeader(ML, Y, CW, SH, "General & Necessary Requirements (Requirements Specified by Customer / Assumptions)");
    Y += SH;

    // Valve Type | Others
    twoCol(Y, "Valve Type", V(d.valve_type), "Others", V(d.valve_type_other), CW * 0.55, CW * 0.45);
    Y += RH;
    twoCol(Y, "Design type", V(d.design_type), "Others", V(d.design_type_other), CW * 0.55, CW * 0.45);
    Y += RH;
    twoCol(Y, "Series", V(d.series), "Others", V(d.series_other), CW * 0.55, CW * 0.45);
    Y += RH;

    // Section No | Valve size | Qty
    cell(ML,              Y, CW * 0.22, RH, "Section No.", V(d.section_no));
    cell(ML + CW * 0.22,  Y, CW * 0.28, RH, "Valve size (NPS)", V(d.valve_size));
    cell(ML + CW * 0.50,  Y, CW * 0.25, RH, "Qty.- (Nos.)", V(d.qty));
    cell(ML + CW * 0.75,  Y, CW * 0.25, RH, "(if Other Specify)", V(d.qty_other));
    Y += RH;

    // Valve pressure class | Others | Painting
    cell(ML,              Y, CW * 0.30, RH, "Valve pressure class", V(d.valve_pressure_class));
    cell(ML + CW * 0.30,  Y, CW * 0.20, RH, "Others", V(d.valve_pressure_class_other));
    cell(ML + CW * 0.50,  Y, CW * 0.30, RH, "Painting Requirement", V(d.painting_req));
    cell(ML + CW * 0.80,  Y, CW * 0.20, RH, "Others -", V(d.painting_req_other));
    Y += RH;

    // ── OPERATING CONDITIONS (ASME B 16.34) ──────────────────────────────────
    secHeader(ML, Y, CW, SH, "Operating Conditions (As per ASME B 16.34)");
    Y += SH;

    const opW = CW / 4; // 47.5mm each
    // Row 1: values
    [
      ["Max. Pre.", V(d.asme_max_pre)],
      ["Min. Pre.", V(d.asme_min_pre)],
      ["Min. Temp.", V(d.asme_min_temp)],
      ["Max. Temp.", V(d.asme_max_temp)],
    ].forEach(([lbl, val], i) => cell(ML + i * opW, Y, opW, RH, lbl, val));
    Y += RH;
    // Row 2: units
    ["Kg/cm²", "Kg/cm²", "°C", "°C"].forEach((unit, i) => valCell(ML + i * opW, Y, opW, RH * 0.7, unit));
    Y += RH * 0.7;

    // Second operating condition row (customer-specified)
    secHeader(ML, Y, CW, SH, "Operating Conditions (specified by customer)");
    Y += SH;
    [
      ["Max. Pre.", V(d.cust_max_pre)],
      ["Min. Pre.", V(d.cust_min_pre)],
      ["Max. Temp.", V(d.cust_max_temp)],
      ["Min. Temp.", V(d.cust_min_temp)],
    ].forEach(([lbl, val], i) => cell(ML + i * opW, Y, opW, RH, lbl, val));
    Y += RH;
    ["Kg/cm²", "Kg/cm²", "°C", "°C"].forEach((unit, i) => valCell(ML + i * opW, Y, opW, RH * 0.7, unit));
    Y += RH * 0.7;

    // Second customer row
    [
      ["Min. Pre.", V(d.cust_min_pre2)],
      ["Max. Temp.", V(d.cust_max_temp2)],
      ["Min. Temp.", V(d.cust_min_temp2)],
      ["", ""],
    ].forEach(([lbl, val], i) => {
      if (lbl) cell(ML + i * opW, Y, opW, RH * 0.7, lbl, val);
      else emptyBox(ML + i * opW, Y, opW, RH * 0.7);
    });
    Y += RH * 0.7;

    // Max field test pressure
    cell(ML, Y, CW * 0.65, RH, "Max. field test pressure (If any Special Requirement by Customer)", V(d.max_field_test_pressure) + (d.max_field_test_pressure ? " Kg/cm²" : ""));
    emptyBox(ML + CW * 0.65, Y, CW * 0.35, RH);
    Y += RH;

    // ── OPERATING CONDITIONS AND VALVE ORIENTATION ────────────────────────────
    secHeader(ML, Y, CW * 0.70, SH, "Operating Conditions and Valve Orientation");
    emptyBox(ML + CW * 0.70, Y, CW * 0.30, SH);
    Y += SH;

    twoCol(Y, "Valve function (Duty)", V(d.valve_function_duty), "Others -", V(d.valve_function_other), CW * 0.50, CW * 0.50);
    Y += RH;
    cell(ML, Y, CW * 0.55, RH, "(if Regulating) specify Sub-Type -", V(d.regulating_sub_type));
    emptyBox(ML + CW * 0.55, Y, CW * 0.45, RH);
    Y += RH;
    cell(ML,            Y, CW * 0.35, RH, "Valve Orientation", V(d.valve_orientation));
    cell(ML + CW * 0.35, Y, CW * 0.30, RH, "Others -", V(d.valve_orientation_other));
    cell(ML + CW * 0.65, Y, CW * 0.35, RH, "Hing Pin", V(d.hing_pin));
    Y += RH;

    // ── VALVE END CONNECTION ──────────────────────────────────────────────────
    secHeader(ML, Y, CW, SH, "Valve End Connection details");
    Y += SH;

    const ecL = CW * 0.38;
    const ecC = (CW - ecL) / 2; // ~26mm each side
    emptyBox(ML, Y, ecL, RH * 0.7);
    valCell(ML + ecL,       Y, ecC, RH * 0.7, "Upstream");
    valCell(ML + ecL + ecC, Y, ecC, RH * 0.7, "Down-Stream");
    Y += RH * 0.7;

    [
      ["End Conn. Type", "upstream_end_conn_type", "downstream_end_conn_type"],
      ["End Conn std.", "upstream_end_conn_std", "downstream_end_conn_std"],
      ["Others (Specify)", "end_conn_others_up", "end_conn_others_dn"],
    ].forEach(([label, upKey, dnKey]) => {
      lblCell(ML, Y, ecL, RH, label);
      valCell(ML + ecL,       Y, ecC, RH, V(d[upKey]));
      valCell(ML + ecL + ecC, Y, ecC, RH, V(d[dnKey]));
      Y += RH;
    });

    twoCol(Y, "Full Round opening required", V(d.full_round_opening), "Others", V(d.end_conn_full_round_others));
    Y += RH;

    // ── FOR B/W END ───────────────────────────────────────────────────────────
    secHeader(ML, Y, CW, SH, "For B/W End, Specify the following Data");
    Y += SH;

    [
      ["Pipe – OD.", "pipe_od"],
      ["B/W Pipe – ID. -", "bw_pipe_id"],
      ["B/W Pipe MOC. -", "bw_pipe_moc"],
      ["B/W Pipe thickness. -", "bw_pipe_thickness"],
      ["Special Chemistry of Pipe Material -", "special_chemistry"],
      ["Specified Min. Yield Strength (SMYS) -", "smys"],
      ["Cladding Requirement -", "cladding"],
    ].forEach(([label, key]) => {
      lblCell(ML, Y, ecL, RH, label);
      valCell(ML + ecL,       Y, ecC, RH, V(d[key + "_upstream"]));
      valCell(ML + ecL + ecC, Y, ecC, RH, V(d[key + "_downstream"]));
      Y += RH;
    });

    // ── DESIGN / TESTING / LEAKAGE etc. ──────────────────────────────────────
    const halfW = CW / 2;
    [
      ["Design Std.", V(d.design_std), "Others", V(d.design_std_other)],
      ["Testing Std.", V(d.testing_std), "Others", V(d.testing_std_other)],
      ["Face to Face", V(d.face_to_face), "Others", V(d.face_to_face_other)],
      ["Operation", V(d.operation), "Others", V(d.operation_other)],
      ["Leakage Class", V(d.leakage_class), "Others", V(d.leakage_class_other)],
      ["Media", V(d.media), "Any Special Flow Requirement", V(d.special_flow_req)],
    ].forEach(([l1, v1, l2, v2]) => {
      twoCol(Y, l1, v1, l2, v2);
      Y += RH;
    });

    // ── NDE REQUIREMENTS ──────────────────────────────────────────────────────
    secHeader(ML, Y, CW, SH, "NDE Requirements");
    Y += SH;

    const ndeA = CW * 0.22, ndeB = CW * 0.22, ndeC = CW * 0.20, ndeD = CW * 0.36;
    [
      ["Radiography",           "radiography"],
      ["Ultrasonic Testing",    "ut"],
      ["Magnetic Particle Test","mpt"],
      ["Penetrant Test",        "pt"],
      ["PMI",                   "pmi"],
    ].forEach(([name, key]) => {
      lblCell(ML, Y, ndeA, RH, name);
      cell(ML + ndeA,            Y, ndeB, RH, "If Req. Specify Extent (%)-", V(d[key + "_extent"]));
      cell(ML + ndeA + ndeB,     Y, ndeC, RH, "Standards", V(d[key + "_std"]));
      cell(ML + ndeA + ndeB + ndeC, Y, ndeD, RH, "Any Special Requirement", V(d[key + "_special"]));
      Y += RH;
    });
    cell(ML, Y, CW * 0.40, RH, "Any Other Req. in NDE", "");
    cell(ML + CW * 0.40, Y, CW * 0.60, RH, "Specify", V(d.nde_other));
    Y += RH;

    // ── MATERIAL OF CONSTRUCTION ──────────────────────────────────────────────
    secHeader(ML, Y, CW, SH, "Material of Construction and Trim Details");
    Y += SH;

    // GAD | QAP | Lifting | Flow Direction
    cell(ML,              Y, CW * 0.22, RH, "GAD Required", V(d.gad_required));
    cell(ML + CW * 0.22,  Y, CW * 0.22, RH, "QAP Required", V(d.qap_required));
    cell(ML + CW * 0.44,  Y, CW * 0.28, RH, "Lifting Hook /Point", V(d.lifting_hook));
    cell(ML + CW * 0.72,  Y, CW * 0.28, RH, "Flow Direction", V(d.flow_direction));
    Y += RH;

    // Valve pressure class | Painting
    cell(ML,              Y, CW * 0.30, RH, "Valve pressure class", V(d.valve_pressure_class_mat));
    cell(ML + CW * 0.30,  Y, CW * 0.20, RH, "Others", V(d.vpc_mat_other));
    cell(ML + CW * 0.50,  Y, CW * 0.30, RH, "Painting Requirement", V(d.painting_req_mat));
    cell(ML + CW * 0.80,  Y, CW * 0.20, RH, "Others -", V(d.paint_mat_other));
    Y += RH;

    // Trim No | Disc
    twoCol(Y, "Trim No.", V(d.trim_no), "Disc", V(d.disc));
    Y += RH;
    // Seat | Body/Bonnet Bolting
    twoCol(Y, "Seat", V(d.seat), "Body / Bonnet / Cover Bolting", V(d.body_bonnet_bolting));
    Y += RH;
    // Gasket | Drain Plug
    twoCol(Y, "Gasket (if required Specify MOC)", V(d.gasket), "Drain Plug", V(d.drain_plug));
    Y += RH;
    // Hing Pin (half width)
    cell(ML, Y, halfW, RH, "Hing Pin", V(d.hing_pin_mat));
    emptyBox(ML + halfW, Y, halfW, RH);
    Y += RH;

    // QSL Level | Applicable Clauses
    twoCol(Y, "QSL Level Required", V(d.qsl_level), "Applicable Clauses of API 6D", V(d.applicable_clauses));
    Y += RH;

    // Note
    doc.setFont("helvetica", "italic");
    doc.setFontSize(5.5);
    doc.text(
      "Note: Check/Ensure that the specific material grade is identified: Yes",
      ML + 1, Y + 3.5
    );
    Y += 5;

    // ═══════════════════════════════════════════════════════════════════════════
    //  PAGE 2  (2 of 3)
    // ═══════════════════════════════════════════════════════════════════════════
    doc.addPage();
    drawPageHeader(2, 3);
    Y = 13;

    // ── ADDITIONAL REQUIREMENTS (API 6D) ─────────────────────────────────────
    secHeader(ML, Y, CW, SH, "Additional Requirements (API 6D)");
    Y += SH;

    [
      ["Vent Connection",                       "vent_connection",        "Drain Connection",                              "drain_connection"],
      ["Support Ribs / Lug",                    "support_ribs",           "Gasket",                                        "gasket_add"],
      ["Body Bonnet Joint facing & Gasket",      "body_bonnet_joint",      "Non-standard Face-to face and End-to-end Dimensions","non_standard_f2f"],
      ["Intermediate Pressures and Temperature Ratings","intermediate_pressures","Valve Bore",                              "valve_bore"],
      ["If Req. Specify Pressure Boundary Bolting—Hardness Testing","pressure_boundary","Alternate Seat Test",              "alternate_seat_test"],
      ["Lifting",                               "lifting_add",            "Use of Assembly Lubricant",                     "assembly_lubricant"],
      ["Drain Connection",                      "drain_connection2",      "Welding Overlay Iron Dilution",                 "welding_overlay"],
    ].forEach(([l1, k1, l2, k2]) => {
      twoCol(Y, l1, V(d[k1]), l2, V(d[k2]));
      Y += RH;
    });

    // Additional one-per-row items
    [
      ["Companion Flanges",                              "companion_flanges"],
      ["End Connectors",                                 "end_connectors"],
      ["Flange Bolting for Studded-outlet End Connectors","flange_bolting"],
      ["Special Requirements (Accessories)",             "special_req_accessories"],
    ].forEach(([lbl, key]) => { cell(ML, Y, CW, RH, lbl, V(d[key])); Y += RH; });

    // ── NACE / AMPP ───────────────────────────────────────────────────────────
    secHeader(ML, Y, CW, SH, "NACE/AMPP Requirements");
    Y += SH;

    twoCol(Y,
      "Sour Service",      V(d.sour_service) + (d.sour_service_std ? "  If Req. Specify Std. " + d.sour_service_std : ""),
      "NACE/AMPP Requirements", V(d.nace_req) + (d.nace_std ? "  If Req. Specify Std. " + d.nace_std : "")
    );
    Y += RH;

    // H2S / chlorides
    const h2sLabel = "If Required Specify % mass of H\u2082S";
    const chloridesLabel = "% mass of chlorides";
    cell(ML,              Y, CW * 0.45, RH, h2sLabel, V(d.h2s_mass));
    cell(ML + CW * 0.45,  Y, CW * 0.55, RH, chloridesLabel, V(d.chlorides_mass));
    Y += RH;

    // ── ADDITIONAL TESTING REQUIREMENTS ──────────────────────────────────────
    secHeader(ML, Y, CW, SH, "Additional Testing Requirements (Specified by Customer)");
    Y += SH;

    [
      ["Hydrostatic Testing",                        "hydrostatic_testing",  "hydrostatic_specify"],
      ["Low Pre. Gas Seat Test",                     "low_pre_gas_seat",     "low_pre_specify"],
      ["High Pre. Gas Test",                         "high_pre_gas",         "high_pre_specify"],
      ["High Pre. Gas Seat Test",                    "high_pre_gas_seat",    "high_pre_gas_seat_specify"],
      ["Extended hydrostatic shell testing duration","extended_hydrostatic", "extended_specify"],
      ["IGC Test",                                   "igc_test",             "igc_specify"],
      ["Any Other",                                  "any_other_test",       "any_other_specify"],
    ].forEach(([label, reqKey, specKey]) => {
      const reqVal  = V(d[reqKey]);
      const specVal = V(d[specKey]);
      cell(ML,              Y, CW * 0.42, RH, label, reqVal ? "If Yes Specify" : "");
      cell(ML + CW * 0.42,  Y, CW * 0.58, RH, "Specify", specVal);
      Y += RH;
    });

    // ── INSPECTION & CERTIFICATION ────────────────────────────────────────────
    secHeader(ML, Y, CW, SH, "Inspection and Certification Requirements");
    Y += SH;

    twoCol(Y, "IBR Certification", V(d.ibr_cert), "Others", V(d.ibr_other));
    Y += RH;
    twoCol(Y, "API Monogram", V(d.api_monogram), "Others", V(d.api_other));
    Y += RH;
    twoCol(Y, "Inspection By", V(d.inspection_by), "If TPI, Specify Name", V(d.tpi_name));
    Y += RH;
    twoCol(Y, "MTC As per", V(d.mtc_as_per), "Others -", V(d.mtc_other));
    Y += RH;
    cell(ML, Y, CW, RH, "Other Certification", V(d.other_certification));
    Y += RH;

    // ── SUPPLEMENTARY DOCUMENTATION ──────────────────────────────────────────
    secHeader(ML, Y, CW, SH, "Supplementary Documentation Requirement");
    Y += SH;
    cell(ML, Y, CW, RH * 1.8, "Legal & Other Applicable Requirements -", V(d.legal_requirements));
    Y += RH * 1.8;

    // ── SERVICING ─────────────────────────────────────────────────────────────
    secHeader(ML, Y, CW, SH, "Servicing / After Sales Servicing Requirement");
    Y += SH;
    cell(ML, Y, CW, RH, "Not Required", V(d.servicing_req));
    Y += RH;

    // Name Plate | LD Clause
    cell(ML, Y, CW, RH, "Name Plate / Tag Plate Requirement ( If any Special)", V(d.name_plate_req));
    Y += RH;
    twoCol(Y, "LD Clause", V(d.ld_clause), "If applicable Specify %", V(d.ld_percentage));
    Y += RH;

    // ── COMMERCIAL TERMS ─────────────────────────────────────────────────────
    secHeader(ML, Y, CW, SH, "Commercial Terms");
    Y += SH;

    [
      ["Contract Delivery Date", V(d.contract_delivery_date), "Packing",         V(d.packing)],
      ["Transportation Mode",    V(d.transport_mode),          "Others",          V(d.transport_other)],
      ["Freight Terms",          V(d.freight_terms),           "Others",          V(d.freight_other)],
      ["TPI Charges",            V(d.tpi_charges),             "Others",          V(d.tpi_other)],
      ["Payment Terms",          V(d.payment_terms),           "Others",          V(d.payment_other)],
    ].forEach(([l1, v1, l2, v2]) => { twoCol(Y, l1, v1, l2, v2); Y += RH; });

    // ── CAPABILITY REVIEW ─────────────────────────────────────────────────────
    secHeader(ML, Y, CW, SH, "Capability Review -");
    Y += SH;

    [
      ["Design Available -",                   V(d.design_available),  "WPS available -",                             V(d.wps_available)],
      ["Capability Review -",                  V(d.capability_review), "Insp & Testing Facility -",                   V(d.insp_testing)],
      ["Machining Facility -",                 V(d.machining_facility),"Qualified Personnel -",                       V(d.qualified_personnel)],
      ["Approved Suppliers -",                 V(d.approved_suppliers),"Pattern Available -",                         V(d.pattern_available)],
      ["Is any Modification required in Pattern ?", V(d.modification_req), "IOM (Instruct and Operation Manual)",    V(d.iom)],
    ].forEach(([l1, v1, l2, v2]) => { twoCol(Y, l1, v1, l2, v2); Y += RH; });

    // ── CHEMICAL COMPOSITION ──────────────────────────────────────────────────
    secHeader(ML, Y, CW, SH, "Chemical Composition");
    Y += SH;

    const compositions = d.chemical_composition || [];
    const numW = CW * 0.06, compW = (CW * 0.50) - numW;
    // Rows 1-3 have two columns; rows 4-5 have only left column
    for (let r = 0; r < 3; r++) {
      valCell(ML, Y, numW, RH, String(r + 1));
      valCell(ML + numW, Y, compW, RH, V(compositions[r]));
      valCell(ML + CW * 0.50, Y, numW, RH, String(r + 4));
      valCell(ML + CW * 0.50 + numW, Y, compW, RH, V(compositions[r + 3]));
      Y += RH;
    }
    for (let r = 3; r < 5; r++) {
      valCell(ML, Y, numW, RH, String(r + 1));
      valCell(ML + numW, Y, compW, RH, V(compositions[r]));
      emptyBox(ML + CW * 0.50, Y, CW * 0.50, RH);
      Y += RH;
    }

    // pH | Other Drain Connectors
    twoCol(Y, "pH", V(d.ph), "Other Drain Connectors", V(d.other_drain_connectors));
    Y += RH;

    // ═══════════════════════════════════════════════════════════════════════════
    //  PAGE 3  (3 of 3)  —  Amendment & Review
    // ═══════════════════════════════════════════════════════════════════════════
    doc.addPage();
    drawPageHeader(3, 3);
    Y = 13;

    // ── AMENDMENT DESCRIPTION TABLE ───────────────────────────────────────────
    secHeader(ML, Y, CW, SH, "AMENDMENT DESCRIPTION");
    Y += SH;

    // Column widths [sum = CW = 190]
    const amW = [CW * 0.05, CW * 0.30, CW * 0.10, CW * 0.28, CW * 0.10, CW * 0.17]; // 190 ✓
    const amHeaders = ["Sr No", "Description of amendments sought", "Date", "Review Particulars", "Date", "Remark"];
    let ax = ML;
    amHeaders.forEach((h, i) => {
      doc.rect(ax, Y, amW[i], RH);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(5.5);
      doc.text(fitText(h, amW[i] - 2, 5.5), ax + 1, Y + RH * 0.65);
      ax += amW[i];
    });
    Y += RH;

    const amendments = d.amendments || [];
    for (let i = 0; i < 5; i++) {
      const am = amendments[i] || {};
      ax = ML;
      [
        String(i + 1),
        V(am.description),
        formatDate(am.date),
        V(am.review_particulars),
        formatDate(am.review_date),
        V(am.remark),
      ].forEach((val, j) => {
        doc.rect(ax, Y, amW[j], RH * 1.8);
        if (val) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(5.5);
          doc.text(fitText(val, amW[j] - 2, 5.5), ax + 1, Y + RH * 0.65);
        }
        ax += amW[j];
      });
      Y += RH * 1.8;
    }
    Y += 3;

    // ── REVIEWED / APPROVED ───────────────────────────────────────────────────
    twoCol(Y, "Reviewed By", V(d.reviewed_by), "Approved By", V(d.approved_by));
    Y += RH + 2;

    secHeader(ML, Y, CW, SH, "Reviewed / Participate By :");
    Y += SH;

    const depts  = ["Planning", "Production", "Quality", "Purchase", "Sales", "Design"];
    const deptW  = CW / depts.length;
    depts.forEach((dept, i) => {
      cell(ML + i * deptW, Y, deptW, RH * 2.5, dept, V(d["dept_" + dept.toLowerCase()]));
    });
    Y += RH * 2.5 + 2;

    // Decision on deviation
    cell(ML, Y, CW, RH, "Decision on deviation if any", V(d.decision_on_deviation));
    Y += RH + 4;

    // Remarks block
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    doc.text("1.", ML, Y);
    doc.setFont("helvetica", "normal");
    const remark1 = "The Contract Review Was Carried out by Cross Functional team and concluded that the Organization is capable to meet the requirements subject to above remarks and deviations if any after successful resolution with Customer..";
    const lines1 = doc.splitTextToSize(remark1, CW - 8);
    doc.text(lines1, ML + 5, Y, { maxWidth: CW - 8 });
    Y += lines1.length * 4 + 2;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    doc.text("2.", ML, Y);
    doc.setFont("helvetica", "normal");
    doc.text("The Previously identified differing requirements if any have been resolved. The MOM duly signed is enclosed.", ML + 5, Y);
    
    // ── Save ──────────────────────────────────────────────────────────────────
    const fileName = `TCDS_API6D_${V(d.tcds_no) || "Certificate"}_${formatDateForFile(d.date || d.tcds_released_on)}.pdf`;
    doc.save(fileName);
    return fileName;

  } catch (error) {
    console.error("Error generating API 6D PDF:", error);
    throw error;
  }
};

export default generateAPI6DPdf;